import { Turbo } from '@hotwired/turbo-rails'

import { loadDeviceKeyPairForDeviceToken } from '../../encryption/encryption'

import AuthenticationTokenBaseController from './authentication_token_base_controller'

/**
 * AuthenticationTokenController
 *
 * This controller handles the authentication token flow for the bridge login page.
 * It shows a login form or a token form depending on whether the token is present.
 *
 * It also handles the authentication of the user using the authentication token.
 * After authentication, the device token is used to load the device key.
 */
export default class AuthenticationTokenController extends AuthenticationTokenBaseController<HTMLFormElement> {
  static targets = ['loginContainer', 'tokenContainer', 'spinnerContainer']

  async connect() {
    super.connect()

    if (this.hasRequiredTargets()) {
      await this.checkTokenPresence()
    }
  }

  /**
   * Shows the login container
   */
  showLoginContainer() {
    this.spinnerContainerTarget.classList.add('hidden')
    this.loginContainerTarget.classList.remove('hidden')
    this.tokenContainerTarget.classList.add('hidden')
  }

  /**
   * Shows the token container
   */
  showTokenContainer() {
    this.spinnerContainerTarget.classList.add('hidden')
    this.loginContainerTarget.classList.add('hidden')
    this.tokenContainerTarget.classList.remove('hidden')
  }

  /**
   * Authenticates the user using the authentication token
   *
   * After authentication, the device token is used to load the device key
   */
  async authenticate() {
    const token = await this.getToken()
    if (!token) {
      console.error('No token available for authentication')
      return
    }

    const { authToken, deviceToken } = token

    try {
      const authenticated = await this.authenticateWithToken(authToken)

      if (authenticated) {
        // unlock encryption using the device token. We can ignore the result. If it fails, the user will be redirected to the unlock screen and use their password, which then onboards the deviceToken
        await loadDeviceKeyPairForDeviceToken(deviceToken)

        // Navigate to continuation page
        Turbo.visit('/continuation')
      } else {
        // if the token is invalid, delete it and redirect to the login page
        await this.deleteToken()
        Turbo.visit('/patient_users/sign_in')
      }
    } catch (error) {
      console.error('Authentication failed:', error)
    }
  }

  /**
   * Authenticates the user using the authentication token
   *
   * After authentication, the user's session is logged in (or not, if the token is invalid)
   *
   * @returns true if the user's session is logged in, false otherwise
   */
  private async authenticateWithToken(authToken: string): Promise<boolean> {
    const response = await fetch('/authentication_tokens/authenticate', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
    })

    return response.ok
  }

  private hasRequiredTargets() {
    return (
      this.hasLoginContainerTarget && this.hasTokenContainerTarget && this.hasSpinnerContainerTarget
    )
  }

  /**
   * Checks if the token is present and shows the appropriate container
   */
  private async checkTokenPresence() {
    if (await this.hasToken()) {
      this.showTokenContainer()
    } else {
      this.showLoginContainer()
    }
  }

  declare loginContainerTarget: HTMLDivElement
  declare hasLoginContainerTarget: boolean
  declare tokenContainerTarget: HTMLDivElement
  declare hasTokenContainerTarget: boolean
  declare spinnerContainerTarget: HTMLDivElement
  declare hasSpinnerContainerTarget: boolean
}
